import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormArray, FormBuilder, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Editor, Toolbar } from "ngx-editor";
import { CommonSelectionInterface } from "src/app/share/interface/common.interface";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { O2OPartnerProtectionService } from "../../services/o2o-partner-protection.service";
import { ValidatorFunction } from "../../../share/function/validator.function";
import {
  O2ODocumentInterface,
  O2OPartnerProtectionAddInterface,
  O2OPartnerProtectionEditInterface,
  O2OPartnerProtectionListingInterface
} from "../../interfaces/o2o-partner.interface";
import { AlertService } from "../../../share/service/alert.service";
import { ENTER } from "@angular/cdk/keycodes";
import { MatChipInputEvent } from "@angular/material/chips";

export interface ProtectionDialogData {
  id: string; // partner id
  protection?: O2OPartnerProtectionListingInterface;
}

@Component({
  selector: "app-add-protection-dialog",
  templateUrl: "./add-protection-dialog.component.html",
  styleUrls: ["./add-protection-dialog.component.scss"]
})
export class AddProtectionDialogComponent implements OnInit, OnDestroy {
  isEditMode = false;
  readonly separatorKeysCodes: number[] = [ENTER];

  form = this.fb.group({
    type: ["", Validators.required],
    insurers: ["", Validators.required],
    title: ["", Validators.required],
    catalog: [[] as File[], [ValidatorFunction.fileSize(5)]],
    description: ["", Validators.required],
    to: this.fb.array([], [Validators.required, Validators.email]),
    cc: this.fb.array([], Validators.email)
  });

  protectionTypes: CommonSelectionInterface[] = [];
  insurers: CommonSelectionInterface[] = [];

  editor: Editor;
  toolbar: Toolbar = [
    ["bold", "italic"],
    ["underline", "strike"],
    ["code", "blockquote", "horizontal_rule"],
    ["ordered_list", "bullet_list"],
    [{ heading: ["h1", "h2", "h3", "h4", "h5", "h6"] }],
    ["link", "image"],
    ["text_color", "background_color"],
    ["align_left", "align_center", "align_right", "align_justify"]
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private alertService: AlertService,
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<AddProtectionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ProtectionDialogData,
    private o2oPartnerProtectionService: O2OPartnerProtectionService
  ) {
    this.isEditMode = !!this.data.protection;
  }

  get toEmails(): FormArray {
    return this.form.get("to") as FormArray;
  }

  get ccEmails(): FormArray {
    return this.form.get("cc") as FormArray;
  }

  get isFormChanged(): boolean {
    if (!this.isEditMode) return true;

    const protection = this.data.protection;
    const { type, insurers, title, description, to, cc, catalog } =
      this.form.value;

    const currentEmails = [...to, ...cc].filter(Boolean).join(";");

    // Check if a new file is selected in the upload component
    const isFilesChanged = catalog && catalog.length > 0;

    // Check if the existing document was toggled for deletion
    const isExistingDocumentDeleted = protection.catalog?.UI_isDeleted;

    return (
      type !== protection.protectionTypeId ||
      insurers !== protection.insurerId ||
      title !== protection.title ||
      description !== protection.description ||
      currentEmails !== (protection.insurerEmails || "") ||
      isFilesChanged ||
      isExistingDocumentDeleted
    );
  }

  ngOnInit(): void {
    this.editor = new Editor();

    this.getProtectionTypes();
    this.handleTypeChanges();
    this.setupValueChangesSubscriptions();

    if (this.isEditMode) {
      this.form.patchValue({
        title: this.data.protection.title,
        description: this.data.protection.description,
        type: this.data.protection.protectionTypeId,
        insurers: this.data.protection.insurerId
      });

      if (this.data.protection.insurerEmails) {
        const emails = this.data.protection.insurerEmails.split(";");
        if (emails.length > 0) {
          this.toEmails.push(this.fb.control(emails[0]));
          emails.slice(1).forEach((email) => {
            this.ccEmails.push(this.fb.control(email));
          });
        }
      }

      this.form.get("catalog").clearValidators();
      this.form.get("catalog").setValidators([ValidatorFunction.fileSize(5)]);
      this.form.get("catalog").updateValueAndValidity();
    }
  }

  ngOnDestroy(): void {
    this.editor.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  getProtectionTypes(): void {
    this.o2oPartnerProtectionService.getProtectionTypes().subscribe((types) => {
      this.protectionTypes = types;
    });
  }

  handleTypeChanges(): void {
    this.form
      .get("type")
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((typeId) => {
        if (typeId) {
          this.o2oPartnerProtectionService
            .getInsurersByProtectionTypeId(typeId)
            .subscribe((insurers) => {
              this.insurers = insurers;
            });
        } else {
          this.insurers = [];
          this.form.get("insurers").setValue("");
        }
      });
  }

  addEmail(event: MatChipInputEvent, field: "to" | "cc"): void {
    const value = (event.value || "").trim().toLowerCase();
    if (!value) return;

    if (this.isDuplicateEmail(value)) {
      this.alertService.openSnackBar("Email already exists");
    } else {
      this.processEmailAddition(value, field);
    }

    if (event.chipInput) {
      event.chipInput.clear();
    }
  }

  removeEmail(index: number, field: "to" | "cc"): void {
    const control = field === "to" ? this.toEmails : this.ccEmails;
    control.removeAt(index);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (!this.form.valid || !this.isFormChanged) {
      return;
    }

    const payload = this.isEditMode
      ? this.getEditPayload()
      : this.getAddPayload();

    const request$ = this.isEditMode
      ? this.o2oPartnerProtectionService.update(this.data.id, payload as O2OPartnerProtectionEditInterface)
      : this.o2oPartnerProtectionService.add(this.data.id, payload as O2OPartnerProtectionAddInterface);

    request$.subscribe({
      next: () => {
        const action = this.isEditMode ? "updated" : "added";
        this.alertService.openSnackBar(`Protection ${action} successfully`);
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.alertService.openSnackBar(err.error);
      }
    });
  }

  toggleDeletion(doc: O2ODocumentInterface): void {
    doc.UI_isDeleted = !doc.UI_isDeleted;

    if (!doc.UI_isDeleted && this.form.value.catalog.length > 0) {
      this.form.controls.catalog.setValue(null);
    }
  }

  private setupValueChangesSubscriptions(): void {
    if (!this.isEditMode) return;

    this.form
      .get("catalog")
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value: File[]) => {
        const existingCatalog = this.data.protection?.catalog;
        if (existingCatalog) {
          existingCatalog.UI_isDeleted = value && value.length > 0;
        }
      });
  }

  private isDuplicateEmail(email: string): boolean {
    const allEmails = [...this.toEmails.value, ...this.ccEmails.value];
    return allEmails.includes(email);
  }

  private processEmailAddition(value: string, field: "to" | "cc"): void {
    const control = field === "to" ? this.toEmails : this.ccEmails;
    const emailControl = this.fb.control(value, [Validators.required, Validators.email]);

    if (emailControl.invalid) {
      this.alertService.openSnackBar("Invalid email format");
      return;
    }

    if (field === "to") {
      control.clear();
    }
    control.push(emailControl);
  }

  private getEditPayload(): O2OPartnerProtectionEditInterface {
    const { catalog, title, type, insurers, description, to, cc } =
      this.form.value;
    const protection = this.data.protection;

    const insurerEmails = [...to, ...cc].filter(Boolean).join(";");

    const payload: O2OPartnerProtectionEditInterface = {
      id: protection.id
    };

    if (type !== protection.protectionTypeId) payload.protectionTypeId = type;
    if (insurers !== protection.insurerId) payload.insurerId = insurers;
    if (title !== protection.title) payload.title = title;
    if (description !== protection.description)
      payload.description = description;
    if (insurerEmails !== (protection.insurerEmails || ""))
      payload.insurerEmails = insurerEmails;
    if (catalog.length > 0) payload.catalog = catalog[0];
    if (protection.catalog?.UI_isDeleted)
      payload.removedCatalogFileId = protection.catalog.documentId;

    return payload;
  }

  private getAddPayload(): O2OPartnerProtectionAddInterface {
    const { catalog, title, type, insurers, description, to, cc } =
      this.form.value;

    const insurerEmails = [...to, ...cc].filter(Boolean).join(";");

    return {
      catalog,
      title,
      description,
      protectionTypeId: type,
      insurerId: insurers,
      insurerEmails
    };
  }
}
